'use client';

import { Alert, Button, Box } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { useAuthStore } from '@/stores/authStore';

// Load basic options to check for errors
const BASIC_OPTION_KEYS: OptionKey[] = ['status', 'visibility'];

/**
 * Global error banner that displays when the app is broken (authentication or core options fail to load).
 * Shows persistent error messages with retry functionality.
 *
 * This is for app-level failures that affect the entire application.
 * For single operation failures (e.g., create, ship), use snackbars instead.
 */
export function AppErrorBanner() {
  const {
    isUnauthenticated: optionsUnauthenticated,
    isUnauthorized: optionsUnauthorized,
    isServerError: optionsServerError,
    errorStatus: optionsErrorStatus,
    retry: retryOptions,
  } = useSelectOptions(BASIC_OPTION_KEYS);

  const { hasServerError: authServerError, serverErrorStatus: authServerErrorStatus, retryAuth } = useAuthStore();

  // Combine error states
  const hasOptionsError = optionsUnauthenticated || optionsUnauthorized || optionsServerError;
  const hasAuthError = authServerError;
  const hasAnyError = hasOptionsError || hasAuthError;

  // Don't show banner if no errors
  if (!hasAnyError) {
    return null;
  }

  const getErrorMessage = () => {
    const messages: string[] = [];

    if (authServerError) {
      messages.push(
        `Authentication service error (${authServerErrorStatus || '500'}). Unable to refresh session or load user data.`
      );
    }

    if (optionsUnauthenticated) {
      messages.push('Unable to load application options. Please sign in to continue.');
    } else if (optionsUnauthorized) {
      messages.push(
        `Unable to load application options (${optionsErrorStatus || '403'}). You may need to sign in or contact your administrator.`
      );
    } else if (optionsServerError) {
      messages.push(
        `Server error (${optionsErrorStatus || '500'}) while loading application options. Please try again.`
      );
    }

    return messages.join(' ');
  };

  const getSeverity = () => {
    if (authServerError || optionsServerError) return 'error' as const;
    if (optionsUnauthorized) return 'warning' as const;
    return 'info' as const;
  };

  const handleRetry = () => {
    if (authServerError) {
      retryAuth();
    }
    if (hasOptionsError) {
      retryOptions();
    }
  };

  return (
    <Box sx={{ width: '100%', position: 'sticky', top: '64px', zIndex: 1100 }}>
      <Alert
        severity={getSeverity()}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={handleRetry}
            startIcon={<Refresh />}
            sx={{ textTransform: 'none' }}
          >
            Retry
          </Button>
        }
        sx={{
          borderRadius: 0,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        {getErrorMessage()}
      </Alert>
    </Box>
  );
}
